import { useState } from 'react';
import type { EPCR, BodySystem, BodySystemAssessment } from '@/types';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoAssessmentFields } from '@/lib/demo-data';
import { cn } from '@/lib/cn';
import { ChevronDown, ChevronRight, Check, ShieldAlert } from 'lucide-react';
import { isSetActive, activeSets } from '@/lib/conditional-cascade';
import { TraumaTab } from './TraumaTab';
import { StrokeSetCard } from '../sets/StrokeSetCard';
import { StemiSetCard } from '../sets/StemiSetCard';
import { RestraintSetCard } from '../sets/RestraintSetCard';
import { RefusalSetCard } from '../sets/RefusalSetCard';
import { DoaSetCard } from '../sets/DoaSetCard';
// CareMatrix moved to ProceduresTab — protocol-driven reporting belongs
// next to the procedures and medications it documents, not next to the
// body-system findings.

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

interface SystemConfig {
  key: BodySystem;
  label: string;
  findings: { key: string; label: string; type: 'toggle' | 'select' | 'text'; options?: string[] }[];
}

const SYSTEMS: SystemConfig[] = [
  {
    key: 'neurological',
    label: 'Neurological',
    findings: [
      { key: 'loc', label: 'Level of Consciousness', type: 'select', options: ['Alert', 'Verbal', 'Pain', 'Unresponsive'] },
      { key: 'gcs_eye', label: 'GCS Eye (1-4)', type: 'select', options: ['1', '2', '3', '4'] },
      { key: 'gcs_verbal', label: 'GCS Verbal (1-5)', type: 'select', options: ['1', '2', '3', '4', '5'] },
      { key: 'gcs_motor', label: 'GCS Motor (1-6)', type: 'select', options: ['1', '2', '3', '4', '5', '6'] },
      { key: 'pupils', label: 'Pupils', type: 'select', options: ['PERRL', 'Unequal', 'Fixed/Dilated', 'Constricted'] },
      { key: 'speech', label: 'Speech', type: 'select', options: ['Normal', 'Slurred', 'Aphasic', 'Incomprehensible'] },
      { key: 'headache', label: 'C/O Headache', type: 'toggle' },
      { key: 'dizziness', label: 'Dizziness', type: 'toggle' },
      { key: 'syncope', label: 'Syncope', type: 'toggle' },
      { key: 'seizure_activity', label: 'Seizure Activity', type: 'toggle' },
    ],
  },
  {
    key: 'respiratory',
    label: 'Respiratory',
    findings: [
      { key: 'rate', label: 'Rate', type: 'select', options: ['Normal', 'Tachypneic', 'Bradypneic', 'Apneic'] },
      { key: 'rhythm', label: 'Rhythm', type: 'select', options: ['Regular', 'Irregular'] },
      { key: 'effort', label: 'Effort', type: 'select', options: ['Normal', 'Labored', 'Shallow', 'Agonal'] },
      { key: 'breath_sounds_left', label: 'Breath Sounds L', type: 'select', options: ['Clear', 'Wheezes', 'Crackles', 'Rhonchi', 'Diminished', 'Absent'] },
      { key: 'breath_sounds_right', label: 'Breath Sounds R', type: 'select', options: ['Clear', 'Wheezes', 'Crackles', 'Rhonchi', 'Diminished', 'Absent'] },
      { key: 'accessory_muscles', label: 'Accessory Muscle Use', type: 'toggle' },
    ],
  },
  {
    key: 'cardiovascular',
    label: 'Cardiovascular',
    findings: [
      { key: 'rate', label: 'Rate', type: 'select', options: ['Normal', 'Tachycardic', 'Bradycardic'] },
      { key: 'rhythm', label: 'Rhythm', type: 'select', options: ['Regular', 'Irregular', 'Irregularly Irregular'] },
      { key: 'twelve_lead', label: '12-Lead Interpretation', type: 'text' },
      { key: 'skin_color', label: 'Skin Color', type: 'select', options: ['Normal', 'Pale', 'Cyanotic', 'Flushed', 'Jaundiced', 'Mottled'] },
      { key: 'skin_temp', label: 'Skin Temperature', type: 'select', options: ['Normal', 'Cool', 'Cold', 'Hot'] },
      { key: 'skin_moisture', label: 'Skin Moisture', type: 'select', options: ['Dry', 'Moist', 'Diaphoretic'] },
      { key: 'peripheral_pulses', label: 'Peripheral Pulses', type: 'select', options: ['Present/Equal', 'Weak', 'Absent', 'Unequal'] },
      { key: 'edema', label: 'Edema', type: 'select', options: ['None', 'Pedal', 'Lower Extremity', 'Sacral', 'Generalized'] },
    ],
  },
  {
    key: 'abdomen',
    label: 'Abdomen',
    findings: [
      { key: 'tenderness', label: 'Tenderness', type: 'select', options: ['None', 'RUQ', 'LUQ', 'RLQ', 'LLQ', 'Epigastric', 'Periumbilical', 'Suprapubic', 'Diffuse'] },
      { key: 'rigidity', label: 'Rigidity', type: 'toggle' },
      { key: 'guarding', label: 'Guarding', type: 'toggle' },
      { key: 'distension', label: 'Distension', type: 'toggle' },
      { key: 'bowel_sounds', label: 'Bowel Sounds', type: 'select', options: ['Normal', 'Hyperactive', 'Hypoactive', 'Absent'] },
    ],
  },
  {
    key: 'musculoskeletal',
    label: 'Musculoskeletal',
    findings: [
      { key: 'body_part', label: 'Body Part', type: 'text' },
      { key: 'deformity', label: 'Deformity', type: 'toggle' },
      { key: 'tenderness', label: 'Tenderness', type: 'toggle' },
      { key: 'swelling', label: 'Swelling', type: 'toggle' },
      { key: 'crepitus', label: 'Crepitus', type: 'toggle' },
      { key: 'rom_limitation', label: 'ROM Limitation', type: 'toggle' },
    ],
  },
  {
    key: 'skin',
    label: 'Skin',
    findings: [
      { key: 'color', label: 'Color', type: 'select', options: ['Normal', 'Pale', 'Cyanotic', 'Flushed', 'Jaundiced'] },
      { key: 'temperature', label: 'Temperature', type: 'select', options: ['Normal', 'Cool', 'Cold', 'Hot'] },
      { key: 'moisture', label: 'Moisture', type: 'select', options: ['Dry', 'Moist', 'Diaphoretic'] },
      { key: 'rash', label: 'Rash', type: 'toggle' },
      { key: 'wound_description', label: 'Wound Description', type: 'text' },
    ],
  },
  {
    key: 'heent',
    label: 'HEENT',
    findings: [
      { key: 'head_tenderness', label: 'Head Tenderness', type: 'toggle' },
      { key: 'facial_symmetry', label: 'Facial Symmetry', type: 'select', options: ['Symmetrical', 'Asymmetrical/Droop'] },
      { key: 'jvd', label: 'JVD', type: 'toggle' },
      { key: 'tracheal_deviation', label: 'Tracheal Deviation', type: 'toggle' },
    ],
  },
  {
    key: 'ob_gyn',
    label: 'OB/GYN',
    findings: [
      { key: 'gestational_age', label: 'Gestational Age (weeks)', type: 'text' },
      { key: 'contractions', label: 'Contractions', type: 'toggle' },
      { key: 'crowning', label: 'Crowning', type: 'toggle' },
      { key: 'bleeding', label: 'Vaginal Bleeding', type: 'toggle' },
    ],
  },
  {
    key: 'behavioral',
    label: 'Behavioral / Psychiatric',
    findings: [
      { key: 'mood', label: 'Mood', type: 'select', options: ['Normal', 'Anxious', 'Depressed', 'Agitated', 'Euphoric', 'Flat'] },
      { key: 'affect', label: 'Affect', type: 'select', options: ['Appropriate', 'Inappropriate', 'Flat', 'Labile'] },
      { key: 'thought_content', label: 'Thought Content', type: 'select', options: ['Normal', 'Disorganized', 'Delusional', 'Paranoid'] },
      { key: 'si', label: 'Suicidal Ideation', type: 'select', options: ['Denies', 'Passive', 'Active - No Plan', 'Active - With Plan'] },
      { key: 'hi', label: 'Homicidal Ideation', type: 'select', options: ['Denies', 'Present'] },
      { key: 'cooperative', label: 'Cooperative', type: 'select', options: ['Cooperative', 'Uncooperative', 'Combative'] },
    ],
  },
];

export function AssessmentTab({ epcr, onUpdate }: Props) {
  const [expandedSystem, setExpandedSystem] = useState<BodySystem | null>('neurological');

  const getAssessment = (system: BodySystem): BodySystemAssessment | undefined => {
    return epcr.assessments.find((a) => a.system === system);
  };

  const updateFinding = (system: BodySystem, findingKey: string, value: string | boolean | number) => {
    const existing = epcr.assessments.find((a) => a.system === system);
    const findings = existing ? { ...existing.findings } : {};
    findings[findingKey] = value;

    const updated: BodySystemAssessment = { system, findings };
    const newAssessments = epcr.assessments.filter((a) => a.system !== system);
    newAssessments.push(updated);
    onUpdate({ assessments: newAssessments });
  };

  const hasFindings = (system: BodySystem): boolean => {
    const assessment = getAssessment(system);
    if (!assessment) return false;
    return Object.values(assessment.findings).some((v) => v !== false && v !== '' && v !== undefined);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2 mb-4">
        <p className="text-sm text-slate-400">
          Tap a body system to expand. Selected findings feed the narrative engine.
        </p>
        <DemoFillButton
          label="Fill assessment demo"
          onFill={() => onUpdate(demoAssessmentFields())}
        />
      </div>

      {SYSTEMS.map((sys) => {
        const expanded = expandedSystem === sys.key;
        const assessment = getAssessment(sys.key);
        const completed = hasFindings(sys.key);

        return (
          <div key={sys.key} className="rounded-lg border border-slate-700 overflow-hidden">
            <button
              onClick={() => setExpandedSystem(expanded ? null : sys.key)}
              className={cn(
                'flex w-full items-center gap-3 px-4 py-3 text-left font-medium min-h-touch transition-colors',
                expanded ? 'bg-slate-800' : 'bg-slate-900 hover:bg-slate-800'
              )}
            >
              {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              <span className={completed ? 'text-brand-300' : 'text-slate-300'}>
                {sys.label}
              </span>
              {completed && <Check size={16} className="ml-auto text-ems-green" />}
            </button>

            {expanded && (
              <div className="space-y-3 border-t border-slate-700 bg-slate-900/50 p-4">
                {sys.findings.map((finding) => (
                  <div key={finding.key}>
                    <label className="mb-1 block text-sm font-medium text-slate-400">
                      {finding.label}
                    </label>
                    {finding.type === 'toggle' && (
                      <button
                        type="button"
                        onClick={() => {
                          const current = assessment?.findings[finding.key] as boolean ?? false;
                          updateFinding(sys.key, finding.key, !current);
                        }}
                        disabled={epcr.locked}
                        className={cn(
                          'rounded-lg px-4 py-2 text-sm font-medium min-h-[2.5rem] transition-colors',
                          (assessment?.findings[finding.key] as boolean)
                            ? 'bg-ems-amber text-white'
                            : 'bg-slate-800 text-slate-500 hover:bg-slate-700'
                        )}
                      >
                        {(assessment?.findings[finding.key] as boolean) ? 'Present' : 'Absent'}
                      </button>
                    )}
                    {finding.type === 'select' && (
                      <div className="flex flex-wrap gap-1.5">
                        {finding.options!.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => updateFinding(sys.key, finding.key, opt)}
                            disabled={epcr.locked}
                            className={cn(
                              'rounded-md px-3 py-1.5 text-sm transition-colors',
                              assessment?.findings[finding.key] === opt
                                ? 'bg-brand-500 text-white'
                                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                            )}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                    {finding.type === 'text' && (
                      <input
                        type="text"
                        value={(assessment?.findings[finding.key] as string) ?? ''}
                        onChange={(e) => updateFinding(sys.key, finding.key, e.target.value)}
                        className="input-field"
                        disabled={epcr.locked}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Conditional sets cascade — appear inline at the assessment stage
          when the cascade engine flags them. The Trauma Set is the
          first one wired up; future LAMS / STEMI / behavioral / OB sets
          will surface here too. Each card explicitly states why it
          appeared so the user understands the trigger. */}
      <ConditionalSetsInline epcr={epcr} onUpdate={onUpdate} />
    </div>
  );
}

/**
 * Renders any clinical-set sub-forms that the cascade engine has
 * activated for this run. Today: Trauma Set when injury terms hit
 * primary impression OR possible_injury = Yes. The Trauma component
 * itself manages MVC / pediatric / fall / assault / burn / penetrating
 * sub-sets internally.
 *
 * Future: drop more cards in here as we wire LAMS, STEMI, behavioral,
 * OB, restraint, refusal, DOA into the cascade engine.
 */
function ConditionalSetsInline({
  epcr,
  onUpdate,
}: {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}) {
  const traumaActive   = isSetActive(epcr, 'trauma');
  const strokeActive   = isSetActive(epcr, 'stroke');
  const stemiActive    = isSetActive(epcr, 'stemi');
  const restraintActive = isSetActive(epcr, 'restraint_use');
  const refusalActive  = isSetActive(epcr, 'refusal');
  const doaActive      = isSetActive(epcr, 'doa');

  const anyActive =
    traumaActive || strokeActive || stemiActive ||
    restraintActive || refusalActive || doaActive;
  if (!anyActive) return null;

  return (
    <section className="mt-4 space-y-3">
      <div className="flex items-center gap-2 border-l-4 border-amber-400 bg-amber-50 px-3 py-2 rounded">
        <ShieldAlert size={16} className="text-amber-700 shrink-0" />
        <div className="text-xs text-amber-900">
          <strong>Conditional documentation triggered.</strong> The cascade
          engine activated the cards below based on the chief complaint,
          primary impression, possible-injury flag, transport decision, or
          procedures performed. Each card explains its trigger and shows the
          downstream consumers it satisfies — captured once, fans out to all.
        </div>
      </div>

      {traumaActive && (
        <div className="border border-amber-300 bg-white rounded p-3">
          <TraumaTab epcr={epcr} onUpdate={onUpdate} />
        </div>
      )}
      {strokeActive    && <StrokeSetCard    epcr={epcr} onUpdate={onUpdate} />}
      {stemiActive     && <StemiSetCard     epcr={epcr} onUpdate={onUpdate} />}
      {restraintActive && <RestraintSetCard epcr={epcr} onUpdate={onUpdate} />}
      {refusalActive   && <RefusalSetCard   epcr={epcr} onUpdate={onUpdate} />}
      {doaActive       && <DoaSetCard       epcr={epcr} onUpdate={onUpdate} />}
    </section>
  );
}
